/** Shared API DTO types. */

export interface SparkPoint {
  date: string;
  value: number;
}

export interface OfficeComparisonRow {
  officeName: string;
  liveOnAthena: boolean;
  firstDecisionTimePreHours: number;
  /** null unless liveOnAthena */
  firstDecisionTimeCurrentHours: number | null;
  finalDecisionTimePreHours: number;
  finalDecisionTimeCurrentHours: number | null;
  /** decisions ÷ active analysts, historical */
  capacityRatioPre: number | null;
  /** null unless liveOnAthena */
  capacityRatioCurrent: number | null;
}

export interface HealthSummary {
  applicationsDecided: {
    total: number;
    approved: number;
    funded: number;
    rejected: number;
    qtdTotal: number;
  };
  avgFirstDecisionTimeHours: { current: number; previousPeriod: number; sparkline: SparkPoint[] };
  avgFinalDecisionTimeHours: { current: number; previousPeriod: number; sparkline: SparkPoint[] };
  exceptionsCount: { pg: number; corp: number; sparkline: SparkPoint[] };
  overrideRate: { current: number; previousPeriod: number; sparkline: SparkPoint[] };
  modelHealth: { status: "green" | "amber" | "red"; label: string };
  officeComparison: OfficeComparisonRow[];
}

/**
 * Ticket-size buckets, matching `*_cost_sub_bucket` in athena_ue_performance.
 *
 * "Over 250K" is the open-ended top bucket. It was missing from the original mock, but it is real
 * (5.9k applications) and it is the slowest and most interesting slice — mean decision time in it
 * runs 3-6x the next bucket down. Filtering it out server-side would have computed the ticket mix
 * over a partial denominator, so the union was widened instead.
 */
export type TicketBucket =
  | "0-10K"
  | "10-25K"
  | "25-50K"
  | "50-100K"
  | "100-150K"
  | "150-200K"
  | "200-250K"
  | "Over 250K";

export const TICKET_BUCKETS: TicketBucket[] = [
  "0-10K",
  "10-25K",
  "25-50K",
  "50-100K",
  "100-150K",
  "150-200K",
  "200-250K",
  "Over 250K",
];

/**
 * Credit grades, matching `risk_rating` in athena_ue_performance.
 *
 * "Other" covers applications with no A/B grade — 123k of them, more than a third of the table.
 * Also absent from the original mock and added for the same reason as "Over 250K".
 */
export type RiskTier = "A" | "B1" | "B2" | "B3" | "Other";

export const RISK_TIERS: RiskTier[] = ["A", "B1", "B2", "B3", "Other"];

export interface TatCell {
  approvedHours: number;
  fundedHours: number;
  rejectedHours: number;
  totalHours: number;
}

export interface TatByTierRow {
  riskTier: RiskTier;
  overall: TatCell;
  byTicketSize: Record<TicketBucket, TatCell>;
}

export interface DecisionRoundView {
  applications: { approved: number; funded: number; rejected: number; total: number };
  tatByTier: TatByTierRow[];
  tatOverall: TatCell;
}

export interface CapacityPoint {
  period: string;
  applicationsDecided: number;
  decidedByAnalyst: number;
  /** 0 everywhere for now — nothing in the schema flags an application as auto-decided. */
  decidedByAthenaAuto: number;
  denominatorMethod: "historical_single_attribution" | "field_touch_multi_attribution";
  volumeIndex: number;
  avgFirstDecisionTimeHours: number;
  avgFinalDecisionTimeHours: number;
  /**
   * The three headcount-derived fields, all null until athena_ue_performance.team_headcount has
   * rows. capacityRatio is decisions ÷ active analysts and no warehouse table records the divisor,
   * so there is nothing to compute — the same gap that makes page 1's capacityRatioPre/Current
   * null. CapacityIndexChart hides the headcount line and the gap band while these are null;
   * do not coerce them to 0, which would draw a capacity gap spanning the entire chart.
   */
  activeHeadcount: number | null;
  capacityRatio: number | null;
  headcountIndex: number | null;
}

export interface TicketMixPoint {
  period: string;
  mixPct: Record<TicketBucket, number>;
  decisionTimeWithinBucket: Record<TicketBucket, number>;
}

export interface ProductivitySummary {
  singleRound: DecisionRoundView;
  multiRound: DecisionRoundView;
  capacitySeries: CapacityPoint[];
  ticketMixSeries: TicketMixPoint[];
  pullThroughRate: { current: number; previousPeriod: number; sparkline: SparkPoint[] };
  withdrawnRate: { current: number; previousPeriod: number; sparkline: SparkPoint[] };
  athenaProcessingTime: { available: boolean; avgHours: number | null; note: string };
}

export interface CounterfactualPoint {
  period: string;
  /** null pre-go-live — this line only exists from go-live forward */
  actualHours: number | null;
  /** fitted trend, present across the full range */
  extrapolatedBaselineHours: number;
}

export interface CounterfactualView {
  metric: "first_decision_time";
  fitQuality: "reliable" | "noisy_use_caution";
  fitNote: string;
  points: CounterfactualPoint[];
  /** extrapolated − actual, most recent period, positive = Athena helped */
  latestAttributableGapHours: number;
}

export interface RunwayScenario {
  name: "current_trend_continues" | "efficiency_plateaus" | "volume_grows_20pct";
  label: string;
  projectedCapacityRatio: { period: string; value: number }[];
}

export interface CapacityRunwayView {
  /**
   * null, and `scenarios` empty, while headcount is unavailable — everything this card draws is a
   * function of capacityRatio. The card renders an unavailable state in that case; see
   * CapacityPoint above.
   */
  ceilingCapacityRatio: number | null;
  ceilingBasisNote: string;
  scenarios: RunwayScenario[];
  /** null = doesn't reach ceiling in the projection window */
  headroomMonthsEstimate: { scenario: RunwayScenario["name"]; monthsUntilCeiling: number | null }[];
}

export interface ProductivityProjection {
  counterfactual: CounterfactualView;
  runway: CapacityRunwayView;
}

/**
 * "Unknown" is what the backend reports today: nothing in athena_ue_performance records a
 * guarantor count, so all real volume lands in that one band and the declared bands stay visibly
 * empty. Same treatment as the exceptions card on page 1 — an honest gap rather than volume
 * spread across categories the data cannot justify.
 */
export type GuarantorBand = "0" | "1" | "2+" | "Unknown";

export const GUARANTOR_BANDS: GuarantorBand[] = ["0", "1", "2+", "Unknown"];

/** "Unknown" for the same reason as GuarantorBand — no asset/equipment type column exists. */
export type AssetType =
  | "Titled Vehicles"
  | "Construction Equipment"
  | "Medical Equipment"
  | "IT & Office Equipment"
  | "Material Handling"
  | "Unknown";

export const ASSET_TYPES: AssetType[] = [
  "Construction Equipment",
  "IT & Office Equipment",
  "Material Handling",
  "Medical Equipment",
  "Titled Vehicles",
  "Unknown",
];

export interface ReadinessThresholds {
  maxExceptionRatePct: number;
  maxOverrideRatePct: number;
  maxMedianDecisionHours: number;
  minVolume: number;
}

export type ReadinessCondition = "exception_rate" | "override_rate" | "decision_time" | "volume";

export interface ReadinessCell {
  ticketBucket: TicketBucket;
  guarantorBand: GuarantorBand;
  riskTier: RiskTier;
  assetType: AssetType;
  applicationsDecided: number;
  medianDecisionHours: number;
  exceptionRatePct: number;
  overrideRatePct: number;
  /** empty array = fully ready */
  failingConditions: ReadinessCondition[];
}

export interface AutomationReadinessMatrix {
  defaultThresholds: ReadinessThresholds;
  /** full cross-product: 7 × 3 × 4 × 5 = 420 rows */
  cells: ReadinessCell[];
}

/* ---------- Exceptions and Overrides (step 3) ---------- */

export type ReasonCode =
  | "Compensating cash flow"
  | "Long-standing relationship"
  | "Strong collateral position"
  | "Additional guarantor support"
  | "Industry expertise / tenure"
  | "Other — analyst note";

/**
 * Placeholder labels — Rishabh will map these to the real override reason-code
 * field once the derived table is live.
 */
export const REASON_CODES: ReasonCode[] = [
  "Compensating cash flow",
  "Long-standing relationship",
  "Strong collateral position",
  "Additional guarantor support",
  "Industry expertise / tenure",
  "Other — analyst note",
];

export type SegmentFilter = "all" | "PG" | "Corp";

export interface OverrideRatePoint {
  period: string;
  overrideRatePg: number;
  overrideRateCorp: number;
}

export interface OverrideDirectionPoint {
  period: string;
  approveOverDecline: number;
  declineOverApprove: number;
}

export interface ReasonCodeCount {
  reasonCode: ReasonCode;
  count: number;
}

export interface ReasonCodeTrendSeries {
  reasonCode: ReasonCode;
  trend: { period: string; count: number }[];
}

export interface ExceptionsByTier {
  tier: "A" | "B" | "C" | "DQ";
  count: number;
  /** unreconciled — leaseworks_exception_coded = 'N' */
  candidateCount: number;
  /** leaseworks_exception_coded = 'Y' */
  confirmedCount: number;
}

export interface AgreementRateTrendPoint {
  period: string;
  agreementRatePg: number;
  agreementRateCorp: number;
}

/**
 * Why a field on this page is empty, so the page can say so rather than imply zero.
 *
 * WHY THIS EXISTS. `dataAvailable: true` sitting beside a 0.0 override rate asserts "no
 * overrides occurred" — a stronger and more wrong claim than "we cannot measure this".
 * The precedent is already on this product: `ProductivitySummary.athenaProcessingTime`
 * is exactly this shape, and the page renders its note as visible caption text.
 *
 * `field` names a key on `ExceptionsOverridesSummary`; render the note next to whatever
 * card or chart reads that key.
 */
export interface SuppressedMetric {
  field: string;
  note: string;
}

export interface ExceptionsOverridesSummary {
  /** false whenever the selected office isn't live — drives a full-page empty state */
  dataAvailable: boolean;
  /**
   * SUPPRESSED — all zeros, with an entry in `caveats`. `athena_recommendation` is a
   * 50/50 coin flip: non-null on 15,599 of 15,676 rows in 2014 (twelve years before
   * go-live), two values, and independent of the actual decision, of risk tier and of
   * time. Do not chart this as a real rate.
   */
  overrideRate: {
    current: number;
    previousPeriod: number;
    currentPg: number;
    currentCorp: number;
    sparkline: SparkPoint[];
  };
  notRatedRate: { current: number; previousPeriod: number; sparkline: SparkPoint[] };
  reconciliation: { candidateCount: number; confirmedCount: number; totalCount: number };
  overrideRateTrend: OverrideRatePoint[];
  overrideDirectionTrend: OverrideDirectionPoint[];
  /** respects the segment filter */
  reasonCodeCurrentPeriod: ReasonCodeCount[];
  /** top 3 by current-period volume, respects segment filter */
  reasonCodeTop3Trend: ReasonCodeTrendSeries[];
  /** respects segment filter */
  exceptionsByTier: ExceptionsByTier[];
  /** always both PG and Corp, ignores segment filter — the comparison is the point */
  agreementRateTrend: AgreementRateTrendPoint[];
  /**
   * Which of the fields above are suppressed, and why. Empty once the underlying data
   * is real. Render each note beside the card or chart named by its `field`.
   */
  caveats: SuppressedMetric[];
}

/* ── Policy Compliance by Rule ─────────────────────────────────────────── */

export type RuleId =
  | "DQ1"
  | "DQ2"
  | "A1"
  | "A2"
  | "A3"
  | "A4"
  | "A5"
  | "B1a"
  | "B1b"
  | "B2"
  | "B4"
  | "C1"
  | "C2";

/** Rule SEVERITY grouping — distinct from RiskTier (the deal's credit grade). */
export type RuleTier = "DQ" | "A" | "B" | "C";

/**
 * What the database can actually evaluate, as distinct from what Credit has written down.
 *
 * WHY THIS EXISTS. Nine of the thirteen rules read fully-populated inputs; four are
 * floors rather than complete counts — B1a and B1b are undeterminable for the ~37% of
 * applications with no time-in-business, B2 is missing its PayNet leg entirely, and A2
 * has no crane-exemption list. Without this field a partial rule renders `count: 0`
 * beside a rule that genuinely found nothing, and "no violations found" is a much
 * stronger claim than "not measured".
 */
export type DetectionStatus =
  | "implemented"
  | "partial_coverage"
  | "definition_disputed"
  | "no_source";

export interface RuleDefinition {
  ruleId: RuleId;
  ruleTier: RuleTier;
  label: string;
  detectionLogic: string;
  isReviewFlag: boolean;
  specialNote: string | null;
  detectionStatus: DetectionStatus;
  /** Why the status is what it is. Present for every non-`implemented` rule. */
  detectionNote: string | null;
}

export interface RuleMetric {
  ruleId: RuleId;
  count: number;
  pctOfApprovedPopulation: number;
  candidateCount: number;
  confirmedCount: number;
  sparkline: SparkPoint[];
}

export interface PrerequisiteGateMetric {
  ruleId: "DQ1" | "DQ2";
  excludedCount: number;
  excludedPctOfTotal: number;
}

export interface PolicyComplianceTierPoint {
  period: string;
  complianceRateA: number;
  complianceRateB1: number;
  complianceRateB2: number;
  complianceRateB3: number;
  /**
   * Applications whose tier-relevant rules could not be evaluated at all — chiefly the
   * ~37% with no time-in-business, which makes B1a and B1b undeterminable rather than
   * compliant. A compliance rate that silently drops them from the denominator reports
   * them as passing, so the four rates above exclude them and this counts them instead.
   *
   * There is deliberately no `complianceRateOther`: 'Other' is not a tier, it is the
   * unrated population (rule A5's finding) plus the C-grades (rule B4's), and a
   * "compliance rate for unrated deals" is not a meaningful series.
   */
  undeterminableCount: number;
}

export interface PgFicoGridRow {
  riskGrade: "A / A1" | "B1" | "B2 / B3";
  under2Years: string;
  twoToFourYears: string;
  fourToFiveYears: string;
  overFiveYears: string;
}

export interface PolicyComplianceSummary {
  dataAvailable: boolean;
  gates: PrerequisiteGateMetric[];
  complianceTrend: PolicyComplianceTierPoint[];
  ruleDefinitions: RuleDefinition[];
  ruleMetrics: RuleMetric[];
  pgFicoGrid: PgFicoGridRow[];
}

/* ── Deal-Level Exceptions Log ─────────────────────────────────────────── */

export interface ExceptionLogRow {
  applicationId: string;
  officeName: string;
  ruleId: RuleId;
  fieldChanged: string;
  valueThatTriggeredFlag: string;
  policyThresholdExpected: string;
  submitAmount: number;
  /** The deal's credit grade — A / B1 / B2 / B3, or Other where there is no rating. */
  riskTier: RiskTier;
  /**
   * null for the ~15% of applications with `pg_corp = 'Not Scored'` — never scored, so
   * neither PG nor Corp. Coercing those to `false` would assert "this is a corporate
   * deal", which is a claim the data does not support. The segment filter treats null
   * as matching neither PG nor Corp.
   */
  pg: boolean | null;
  /** Historical data carries user IDs only, never names. */
  analystUserId: string;
  timestamp: string;
  reconciliationStatus: "candidate" | "confirmed";
  /**
   * Measured from the DATA ANCHOR (the latest decision in the warehouse), not wall-clock
   * now — the same clock every other card uses. The two differ by months while ingest
   * lags, so a wall-clock figure would report every open row as long overdue.
   */
  daysUnreconciled: number | null;
  reconciledAt: string | null;
  reconciledNote: string | null;
}

export interface ExceptionsLogSummary {
  dataAvailable: boolean;
  /** Tier A candidate rows only — never reacts to the page's own filters. */
  openTierACount: number;
  avgDaysUnreconciledTierA: number;
  rows: ExceptionLogRow[];
}

/* ── Dealer and Channel Quality ────────────────────────────────────────── */

export interface DealerCreditQuality {
  /** Placeholder code pending the real application_source → dealer mapping. */
  dealerId: string;
  modeRiskTier: RiskTier;
  /** Numeric encoding for charting: A=4, B1=3, B2=2, B3=1. */
  riskTierScore: number;
  approvalRatePct: number;
  volumeCount: number;
  volumeSharePct: number;
}

export interface DealerVelocity {
  dealerId: string;
  medianFirstDecisionHours: number;
  medianFinalDecisionHours: number;
  /** Real, from detected policy exceptions. A floor — see `DetectionStatus`. */
  exceptionRatePct: number;
  /**
   * Always null today. `athena_recommendation` is a 50/50 coin flip (non-null on 2014
   * applications, twelve years before go-live, and independent of the actual decision),
   * so a per-dealer override rate would be thousands of independent-looking
   * confirmations of noise. Render a dash, as `DealerOfficeBreakdown.exceptionRatePct`
   * already does for a not-live office.
   */
  overrideRatePct: number | null;
  avgTicketSize: number;
}

export interface DealerOfficeBreakdown {
  officeName: string;
  volumeCount: number;
  approvalRatePct: number;
  /** null when the office isn't live yet — credit quality still populated. */
  exceptionRatePct: number | null;
}

export interface DealerQualitySummary {
  creditQuality: DealerCreditQuality[];
  velocity: { dataAvailable: boolean; rows: DealerVelocity[] };
  earlyDelinquency: { available: boolean; note: string };
  officeBreakdownByDealer: Record<string, DealerOfficeBreakdown[]>;
}

/* ── Agreement Rate and Credit Culture ─────────────────────────────────── */

export type IndustryCode =
  | "Construction"
  | "Transportation & Trucking"
  | "Manufacturing"
  | "Medical & Healthcare"
  | "Waste Management"
  | "Restaurant & Food Service";

export interface AgreementRatePoint {
  period: string;
  agreementRateSmallTicket: number;
  agreementRateRest: number;
}

export interface AgreementRateByBucketPoint {
  period: string;
  byBucket: Record<TicketBucket, number>;
}

export interface CreditCultureSeries {
  industryCode: IndustryCode;
  trend: { period: string; agreementRate: number }[];
}

/**
 * What the three series on this page actually contain.
 *
 * The page was specified around "agreement rate" — how often an analyst agreed with
 * Athena — and that number cannot be computed honestly: `athena_recommendation` is a
 * 50/50 coin flip. But every DIMENSION the page slices by is real (the $7,500 cut, the
 * eight ticket buckets, PG/Corp, month, and all six industry codes), and the question
 * it exists to answer — is credit culture drifting by industry and ticket size — is
 * answered just as well by approval-rate drift, with data instead of noise.
 *
 * So the series carry approval rate and say so. Read `metricLabel` for axis titles
 * rather than hardcoding "Agreement rate", or the page will mislabel real data.
 */
export type CultureMetric = "approval_rate" | "agreement_rate";

export interface AgreementCultureSummary {
  dataAvailable: boolean;
  smallTicketThresholdDollars: number;
  /** Which metric the three series below carry. */
  metric: CultureMetric;
  /** Human label for axes and legends, e.g. "Approval rate". */
  metricLabel: string;
  /** Why this metric and not the other. Render it as visible caption text. */
  metricNote: string;
  /** Respects the page-level PG/Corp segment. */
  agreementRateTrend: AgreementRatePoint[];
  /** Respects the page-level PG/Corp segment. */
  agreementRateByBucket: AgreementRateByBucketPoint[];
  /** Always aggregate across PG/Corp — industry is the cut here, segment isn't. */
  creditCultureTrend: CreditCultureSeries[];
}

/* ── Alert rules (Manage Alerts sub-step) ──────────────────────────────── */

export type AlertRuleScope =
  | { type: "policy_rule"; ruleId: RuleId; thresholdDaysUnreconciled: number }
  | {
      type: "agreement_rate";
      segment: "small_ticket" | "rest" | "pg" | "corp" | "all";
      thresholdPct: number;
    };

export interface AlertRule {
  alertRuleId: string;
  scope: AlertRuleScope;
  /** Array of officeName when not "all". */
  officeScope: "all" | string[];
  notificationRecipients: string[];
  active: boolean;
  lastTriggeredAt: string | null;
}

export interface AlertRuleProposal {
  proposalId: string;
  status: "PENDING" | "APPROVED" | "REJECTED" | "SUPERSEDED";
  /** null = new rule, populated = modifying an existing one. */
  alertRuleId: string | null;
  scope: AlertRuleScope;
  officeScope: "all" | string[];
  notificationRecipients: string[];
  active: boolean;
  reason: string;
  proposedByRole: string;
  reviewedAt: string | null;
  reviewNotes: string | null;
}

export interface OfficeAuditEntry {
  entryId: string;
  officeName: string;
  fieldChanged: "liveOnAthena" | "goLiveDate" | "note";
  oldValue: string;
  newValue: string;
  changedBy: string;
  changedAt: string;
}
